/* Counterparty detail — /sales/counterparties/:id. Spec/03 archetype B (the shared `dp-*` chrome
   in `component/detail-page.scss`): a non-sticky head + a `minmax(0,720px) 320px` grid of a tabbed
   main column and a reference rail.

   One object load: `GET excalibur/counterparties/:id/` via `getAbortable` + an `AbortController`,
   as `counterparties/List.js` reads the list. `getAbortable` resolves the parsed body (or null on
   401/403, where it has already redirected to /login): a 404 or a body with no `id` is "not found",
   any other failure is an error state with a Retry. `Page` keys the card by the route's id, so
   another counterparty is a fresh mount, not a reused one.

   Writing is INLINE, one field at a time (`component/InlineField.js`, spec/03 "Inline field
   editing"): every writable row carries a pencil; it opens that field's own control in place, with
   Save / Cancel right under it, and nothing else on the card changes. Writable: the six
   `EDIT_FIELDS` — assigned_supervisor, client_type, client_category, sales_channel,
   start_cooperation_at, lifecycle_status (with the lifecycle's manual flag, `lifecycle_is_manual`,
   as one editor) — the company's additional fields (`custom_fields`), and a photo REPLACEMENT (it
   cannot be cleared). name, address, phone_number and department belong to 1С and are read-only
   here: the API silently ignores any other key (no 400), so nothing else is ever sent — an extra
   key would be a no-op that only reads as a change. Who may write is `CAN_EDIT_ROLES` — the API's
   own permission set, there being no finer right on the record; anyone else sees no pencils.

   A save is ONE request carrying that field alone: a JSON `PATCH` of what changed — a cleared
   field goes as `null`, never `""` (a blank date is a 400) — or, for the photo, a multipart `PATCH`
   of `photo` through `callForm` (`call` always `JSON.stringify`s, and a File cannot survive that).
   Only-changed is load-bearing, not thrift: the API marks the lifecycle as set by hand when the
   status it receives differs from the stored one, so sending back a stale, untouched status would
   flip `lifecycle_is_manual`. Success is `isOk` AND a record with an `id` — a failed write hands
   back a TRUTHY `failedResponse()`, and a 404 a parsed `{detail}` that `isOk` passes. The response
   is the full record in read form, so the card refreshes from it with no second GET. `item` is
   always the SAVED record: the draft lives in `editor` and never touches it, so the head, the rail
   and every other row keep stating what the server knows.

   A 400 is the raw `Response` (`action.js`); its per-key body is read raw. The refused field keeps
   its editor open with the draft and one translated phrase under the control — the API's own
   English message is never shown; a key with nowhere to show falls back to the toast.

   Back preserves the list's filters: the list rides its whole filter set in the query string, so
   the row that opened this page hands that `search` over in route state and the breadcrumb pushes
   it back. Opened directly by URL there is no state, so Back goes to the bare list. Leaving the card
   with an unsaved draft or an unsent comment asks first (`leaveTo`); a reload asks through
   `beforeunload`. */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Avatar, Button, Card, DatePicker, Empty, Flex, Modal, Select, Skeleton, Space, Switch, Tag, Tooltip, Typography, Upload } from "antd";
import {
  ApartmentOutlined,
  EditOutlined,
  ExceptionOutlined,
  FileTextOutlined,
  LinkOutlined,
  NumberOutlined,
  CommentOutlined,
  ContactsOutlined,
  ProfileOutlined,
  PushpinOutlined,
  RightOutlined,
  RollbackOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import { KpiStrip } from "../../component/detail-page";
import EntitySelect from "../../component/EntitySelect";
import InlineField from "../../component/InlineField";
import { dash, fullName, initialsOf, PersonCell } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE, ORDERS_PAGE, SIDE_NAV, SUPERVISOR_DETAIL, TRANSLATION_DOWNLOADER, VISIT_PAGE } from "../../config/lang";
import { call, callForm, getAbortable, isOk } from "../../util/action";
import dayjs from "../../util/dayjs";
import { relativeTime } from "../../util/relative-time";
import { translate } from "../../util/util";

import AddressesTab, { NoPointsNotice } from "./AddressesTab";
import { cfDisplay, cfEditor, cfHint, cfWire } from "./customFields";
import CommentsTab from "./CommentsTab";
import ContactsTab from "./ContactsTab";
import DebtsTab from "./DebtsTab";
import LinksSection from "./LinksSection";
import OrdersTab from "./OrdersTab";
import PaymentsTab from "./PaymentsTab";
import ReturnsTab from "./ReturnsTab";
import { linkClick, money, ReadOnlyTag, TabHead } from "./tabParts";
import { LIFECYCLE_STATUSES, lifecycleTags as sharedLifecycleTags, statusTag as sharedStatusTag } from "./status";
import "./counterparties.scss";

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

/* The seven keys `PATCH excalibur/counterparties/:id/` accepts besides `photo`. The first four are
   references the card holds as objects (`{id, first_name, last_name, photo}` for the supervisor,
   `{id, label}` for a dictionary value) and the API takes as a bare id. */
const REF_FIELDS = ["assigned_supervisor", "client_type", "client_category", "sales_channel"];
const EDIT_FIELDS = [...REF_FIELDS, "start_cooperation_at", "lifecycle_status"];

/* Who may write the card — the roles `CounterpartyViewSet.permission_classes` lets PATCH it. The
   API has no per-record right beyond the role, so this is the whole rule; a role outside it sees
   the values without pencils. */
const CAN_EDIT_ROLES = ["supervisor", "supervisor_manager", "sales_representative", "order_manager"];

/* The editable slice in the shape the API takes it, so the dirty check ignores every read-only
   field and "no value" is always `null`. A File is invisible to JSON.stringify, so a staged photo
   is tracked by its own flag rather than here. */
const editable = o => Object.fromEntries(EDIT_FIELDS.map(key => [key, (REF_FIELDS.includes(key) ? o[key]?.id : o[key]) ?? null]));

/* The additional fields that differ from the pristine record, as `custom_fields` items in the
   card's order — that order is what a 400's per-item errors are matched back to. */
const changedCustomFields = (item, original) => {
  const was = Object.fromEntries((original.custom_fields || []).map(f => [f.id, cfWire(f)]));
  return (item.custom_fields || [])
    .filter(f => JSON.stringify(cfWire(f)) !== JSON.stringify(was[f.id] ?? null))
    .map(f => {
      const value = cfWire(f);
      return { field_id: f.id, value: f.type === "text" && value !== null ? value.trim() : value };
    });
};

/* What the API decides about `lifecycle_is_manual` when the key is NOT sent — its rule from before
   the key was writable: a stage changed to another one → manual, a stage cleared → automatic, an
   untouched stage → the saved flag stays. */
const impliedManual = (item, original) => {
  const stage = item.lifecycle_status ?? null;
  if (stage === (original.lifecycle_status ?? null)) return Boolean(original.lifecycle_is_manual);
  return stage !== null;
};

/* Only what differs from the pristine record — the PATCH body. Additional fields ride in the same
   JSON request as `custom_fields`; with nothing else changed, that key is the whole body.
   `lifecycle_is_manual` is sent ONLY when the switch says something the API would not decide on its
   own (`impliedManual`): switching manual off, switching it on with the stage untouched, or a new
   stage with the switch turned off. A new stage with the switch on (as picking it turns it on) goes
   without the key: the API (where `lifecycle_is_manual` is writable) marks the stage as manual on
   its own, so the body stays the minimal diff. */
const changedFields = (item, original) => {
  const now = editable(item);
  const was = editable(original);
  const body = Object.fromEntries(EDIT_FIELDS.filter(key => now[key] !== was[key]).map(key => [key, now[key]]));
  const manual = Boolean(item.lifecycle_is_manual);
  if (manual !== impliedManual(item, original)) body.lifecycle_is_manual = manual;
  const custom = changedCustomFields(item, original);
  if (custom.length) body.custom_fields = custom;
  return body;
};
/* The editor key of an additional field. */
const cfKey = id => `cf:${id}`;
/* Fixed picker scopes, as module constants: `EntitySelect` keys its fetch callbacks on the
   `extraQuery` identity, so an inline literal would rebuild them on every render. The three
   dictionaries share one endpoint and differ by `kind`; it lists live values only (a deleted one
   is soft-deleted and gone from the list). `is_active` keeps deactivated supervisors out. The API
   refuses to newly assign either; one already on the card still shows by name, from `seed` (the
   card sends `{id, label}` even for a deleted value), and is never re-sent unless changed. */
const SUPERVISOR_QUERY = { is_active: true };
const DICTIONARY_QUERY = {
  client_type: { kind: "client_type" },
  client_category: { kind: "client_category" },
  sales_channel: { kind: "sales_channel" },
};

/* The control's DOM id, by editor key; an additional field's is `customFields.js`'s own. */
const fieldId = key => (key.startsWith("cf:") ? `cp-field-cf-${key.slice(3)}` : `cp-field-${key}`);

/* A long value is cut to this many lines, with "Show in full" under it (`InlineField`'s `clamp`). */
const CLAMP_LINES = 3;

/* Sections, navigated by the visit page's rail-of-pills pattern (`vd-rail` + `vd-sec`) rather than
   antd's underline `<Tabs>`, so the counterparty card and the visit review read as one product.
   One section is mounted at a time (only `sectionBody` renders), same as the visit's rail. */
const SECTIONS = [
  { key: "overview", labelKey: "tab_overview", icon: <ProfileOutlined /> },
  /* The address book — spec/02's icon map; the nav's person glyphs (`Idcard`, `Team`) stay distinct. */
  { key: "contacts", labelKey: "tab_contacts", icon: <ContactsOutlined /> },
  /* The points glyph (spec/02's `pin_drop`): the tab opens on the counterparty's retail points. */
  { key: "addresses", labelKey: "tab_addresses", icon: <PushpinOutlined /> },
  { key: "orders", labelKey: "tab_orders", icon: <ShoppingCartOutlined /> },
  { key: "returns", labelKey: "tab_returns", icon: <RollbackOutlined /> },
  /* The glyph the visit page gives its payments section (`visit/View.js`). */
  { key: "payments", labelKey: "tab_payments", icon: <FileTextOutlined /> },
  /* A document with a "!" — the Payments tab's plain document, unpaid (spec/02's icon map). */
  { key: "debts", labelKey: "tab_debts", icon: <ExceptionOutlined /> },
  /* The speech bubble with lines — spec/02; `MessageOutlined` is a contact's Viber. */
  { key: "comments", labelKey: "tab_comments", icon: <CommentOutlined /> },
];
/* The card's sections, as `?tab=` names them. */
const TAB_KEYS = SECTIONS.map(section => section.key);
/* The document tabs' heading: where "All …" goes and what it says (`tabParts.js`'s `TabHead`).
   Payments and debts have no list of their own, so their heading is "Read-only" and the window
   alone — for debts, "open debts" rather than a date window. */
const DOC_TABS = {
  orders: { listPath: "/sales/orders", allKey: "orders_all" },
  returns: { listPath: "/sales/returns", allKey: "returns_all" },
  payments: {},
  debts: { windowKey: "debts_window" },
};

/* The sync state is the card's alone (the list does not show it); the lifecycle tones, the label
   map and the pill itself are shared with the list — `./status`. */
const SYNC_TONE = { synced: "ok", pending: "wait", failed: "no" };

/* `metrics/`'s per-status split of `orders_90d`, in the order the tooltip lists it. Cancelled
   orders are not in the number at all, which the tooltip says. */
const ORDER_STATUSES = ["new", "processing", "sent_for_delivering", "closed"];

/* The card proper, mounted once per counterparty: `Page` below keys it by the route's id, so moving
   to another counterparty (a linked one, Back / Forward) starts it afresh — every state, every tab
   and its modals — and a request still in flight for the previous card lands on an unmounted
   component instead of on this one. */
const CardPage = ({ id }) => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector(state => state.me?.language || "en");
  const role = useSelector(state => state.me?.role);
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);

  /* The SAVED record, always — see the header. */
  const [item, setItem] = useState(null);
  /* The Comments tab's unsent note — here, above the tabs, so switching tabs keeps it (a tab is
     mounted only while open). Cleared when the card changes. */
  const [commentDraft, setCommentDraft] = useState("");
  /* The draft comment's images (`CommentsTab.js`): each an object URL of its JPEG, released here
     when the card goes — the tab releases the ones removed or sent. */
  const [commentImages, setCommentImages] = useState([]);
  const commentImagesRef = useRef(commentImages);
  commentImagesRef.current = commentImages;
  useEffect(() => () => commentImagesRef.current.forEach(image => URL.revokeObjectURL(image.url)), []);
  const [status, setStatus] = useState("loading"); // loading | notfound | error | ready
  /* Bumped by the error state's Retry. */
  const [reloadKey, setReloadKey] = useState(0);
  /* `GET …/metrics/` — the 90-day order count for the head strip, the rail's per-tab `counts` and
     the document tabs' `window_from`. Its own request beside the card's, so a slow or failed one
     never holds the card back: the tile shows a skeleton, then the number or a dash (no toast — the
     card itself is fine). Fetched per counterparty, and again after a contact, address or comment
     is created or removed (`loadMetrics`), so the counters follow without any local arithmetic. */
  const [metrics, setMetrics] = useState({ status: "loading", data: null }); // loading | ok | error

  /* The open section lives in the URL (`?tab=`), so a reload, a shared link and the order page's
     Back all land on it. Switching REPLACES the entry — a tab is not a place in history — and
     carries the route state along, or the breadcrumb would lose the list's filters. Anything
     unknown reads as the Overview. */
  const tabParam = new URLSearchParams(location.search).get("tab");
  const tab = TAB_KEYS.includes(tabParam) ? tabParam : "overview";
  const selectTab = key => history.replace({ pathname: location.pathname, search: `?tab=${key}` }, location.state);
  /* A payment's debt opens the Debts tab pointed at that debt (`?tab=debts&debt=<id>`) — a tab
     switch like `selectTab`, so the same route state rides along. The id is in the URL rather than
     in state so it needs no merging with the card's state and survives a reload; picking any tab
     from the rail writes a bare `?tab=` and drops it. */
  const debtTo = debtId => ({ pathname: location.pathname, search: `?tab=debts&debt=${encodeURIComponent(debtId)}`, state: location.state });
  const debtParam = new URLSearchParams(location.search).get("debt");
  /* AD-4's "Create point" (`?tab=addresses&create=point`): the Addresses tab opens the right modal
     once its address list is in, then drops the parameter — a reload does not reopen it. A URL
     parameter, as `&debt=`, because the notice sits in the head while the addresses load in the tab. */
  const createIntent = new URLSearchParams(location.search).get("create") === "point";
  const openCreatePoint = () => history.replace({ pathname: location.pathname, search: "?tab=addresses&create=point" }, location.state);
  const createIntentDone = useCallback(() => history.replace({ pathname: location.pathname, search: "?tab=addresses" }, location.state), [
    history,
    location.pathname,
    location.state,
  ]);

  /* `getAbortable`, as the list uses it: the controller aborts on unmount. A 404 (or a body with
     no id) is "not found"; anything else failed to load and offers a retry (spec/03 "Error"). */
  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    getAbortable(`excalibur/counterparties/${id}/`, controller.signal)
      .then(response => {
        if (!response) return; // 401/403 — already redirected to /login
        if (!response.id) {
          setStatus("notfound");
          return;
        }
        setItem(response);
        setStatus("ready");
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setStatus(err.message === "HTTP 404" ? "notfound" : "error");
      });
    return () => controller.abort();
  }, [id, reloadKey]);

  /* A re-read keeps what is shown until the new answer lands (no skeleton, no vanishing counters),
     and a failed re-read keeps the last good figures. */
  const metricsController = useRef(null);
  const loadMetrics = useCallback(() => {
    metricsController.current?.abort();
    const controller = new AbortController();
    metricsController.current = controller;
    getAbortable(`excalibur/counterparties/${id}/metrics/`, controller.signal)
      .then(response => {
        if (!response) return; // 401/403 — already redirected to /login
        if (Number.isFinite(response.orders_90d)) setMetrics({ status: "ok", data: response });
        else setMetrics(prev => (prev.status === "ok" ? prev : { status: "error", data: null }));
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setMetrics(prev => (prev.status === "ok" ? prev : { status: "error", data: null }));
      });
  }, [id]);
  useEffect(() => {
    setMetrics({ status: "loading", data: null });
    loadMetrics();
    return () => metricsController.current?.abort();
  }, [loadMetrics]);

  /* The list's `?…` ride-along, so Back returns to the exact filtered view it was opened from. */
  const backSearch = location.state?.from || "";
  const backTo = `/sales/counterparties${backSearch}`;

  /* Copy the card's own URL. `navigator.clipboard` is absent on insecure origins and can reject
     without a user gesture / permission, so the write is guarded and every failure surfaces one
     error toast rather than throwing. */
  const copyLink = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(window.location.href);
      Alert.success(t("card_copied"), { position: "top-right" });
    } catch {
      Alert.error(t("card_copy_error"), { position: "top-right" });
    }
  };

  /* ---------------------------------------------------------------- editing */

  /* One field at a time. `editor` is the open field: `key` is an `EDIT_FIELDS` name, `"lifecycle"`
     (stage + manual switch, one editor), `cf:<id>` (an additional field) or `"photo"`; `draft` the
     value being edited in the card's own shape — for the lifecycle `{lifecycle_status,
     lifecycle_is_manual}`, for the photo the staged `File` (with `preview`, its object URL);
     `error` the one translated phrase a refused save left under the control. */
  const [editor, setEditor] = useState(null);
  const editorRef = useRef(editor);
  editorRef.current = editor;

  /* The record as it would be with the draft applied — what `changedFields` diffs against `item`. */
  const withDraft = (record, ed) => {
    if (!ed || ed.key === "photo") return record;
    if (ed.key === "lifecycle") return { ...record, ...ed.draft };
    if (ed.key.startsWith("cf:")) {
      const id = ed.key.slice(3);
      return { ...record, custom_fields: (record.custom_fields || []).map(f => (f.id === id ? { ...f, value: ed.draft } : f)) };
    }
    return { ...record, [ed.key]: ed.draft };
  };
  /* Dirty is a real comparison (spec/03), by the same diff the request is built from. */
  const patch = item && editor && editor.key !== "photo" ? changedFields(withDraft(item, editor), item) : {};
  const dirty = Boolean(editor) && (editor.key === "photo" ? Boolean(editor.draft) : Object.keys(patch).length > 0);
  const busy = Boolean(editor?.busy);
  /* While one field holds unsaved changes (or is saving) every other pencil is locked — one change
     at a time; an open editor with nothing changed simply yields to the next pencil. */
  const locked = dirty || busy;
  const isEditing = key => editor?.key === key;

  const draftFor = key => {
    if (key === "lifecycle") return { lifecycle_status: item.lifecycle_status ?? null, lifecycle_is_manual: Boolean(item.lifecycle_is_manual) };
    if (key.startsWith("cf:")) return (item.custom_fields || []).find(f => f.id === key.slice(3))?.value ?? null;
    return item[key] ?? null;
  };
  const dropPreview = ed => ed?.preview && URL.revokeObjectURL(ed.preview);
  const startEdit = key => {
    if (locked) return;
    setEditor(prev => {
      dropPreview(prev);
      return { key, draft: draftFor(key), busy: false, error: null, preview: null };
    });
  };
  /* Cancel and Esc drop the draft at once — one field, on screen, cheap to redo (the "discard?"
     dialog is for leaving the card). */
  const cancelEdit = () => {
    if (busy) return;
    setEditor(prev => {
      dropPreview(prev);
      return null;
    });
  };
  /* Changing the draft withdraws the refusal it was marked with — the value it was refused for is gone. */
  const setDraft = draft => setEditor(prev => (prev ? { ...prev, draft, error: null } : prev));
  const setLifecycle = partial => setEditor(prev => (prev ? { ...prev, draft: { ...prev.draft, ...partial }, error: null } : prev));
  /* Picking a stage is setting it by hand, so the switch turns on where the user can see it (and
     can turn it off again before saving); clearing the stage turns it off — manual needs a stage. */
  const setStage = value => setLifecycle({ lifecycle_status: value, lifecycle_is_manual: value !== null });
  /* EntitySelect hands back the full option object (or null). Store only what the card reads and
     the `seed` re-labels from — the API's own read shape — and `null` to clear the field. */
  const pickSupervisor = option =>
    setDraft(
      option ? { id: idOf(option), first_name: option.first_name, last_name: option.last_name, photo: option.photo ?? null, label: option.label } : null
    );
  const pickValue = option => setDraft(option ? { id: idOf(option), label: option.label || option.name } : null);
  /* The photo's pencil opens the file dialog; a chosen file IS the open editor (nothing to type).
     Its preview is an object URL, released when the editor closes or the card unmounts. */
  const stagePhoto = file =>
    setEditor(prev => {
      dropPreview(prev);
      return { key: "photo", draft: file, busy: false, error: null, preview: URL.createObjectURL(file) };
    });
  useEffect(() => () => dropPreview(editorRef.current), []);

  /* A reload or a closed tab with an unsaved draft asks first — the browser's own dialog. */
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = e => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  /* Linked counterparties and points are written through their own endpoints, not the field
     editor's PATCH, and work while a field is open. Their answer is merged — that part alone — into
     the record: `dirty` compares the open editor's draft only, so a draft keeps every unsaved
     change. Unlinking a point answers with the full card; only its points and their count are
     taken. */
  const merge = fn => setItem(prev => (prev ? fn(prev) : prev));
  const mergePoints = card => {
    const points = { points: Array.isArray(card.points) ? card.points : [], points_count: card.points_count ?? 0 };
    merge(prev => ({ ...prev, ...points }));
  };
  /* AD-3: a point made from (or tied to) an address joins the block — once: tying a point the
     counterparty already had changes nothing — in the API's order (active first, then by name). */
  const mergePoint = point =>
    merge(prev => {
      if (!point || (prev.points || []).some(p => p.id === point.id)) return prev;
      const points = [...(prev.points || []), point].sort(
        (a, b) => Number(b.is_active !== false) - Number(a.is_active !== false) || String(a.name).localeCompare(String(b.name)) || a.id - b.id
      );
      return { ...prev, points, points_count: (prev.points_count ?? 0) + 1 };
    });
  const mergeLinks = list => merge(prev => ({ ...prev, linked_counterparties: list }));
  /* A linked counterparty's card, carrying THIS card's list filters on, so its breadcrumb returns
     to the same filtered list (the browser's Back returns here). */
  const linkedPath = otherId => `/sales/counterparties/${otherId}`;
  const linkTo = otherId => ({ pathname: linkedPath(otherId), state: { from: backSearch } });
  /* Every way out of the card goes through here: a linked counterparty, and every link in the
     Orders tab and its modal. Leaving with unsaved edits asks first — the same dialog Cancel shows;
     the next page would otherwise drop the form without a word. It navigates itself (`event` may
     be null, from a modal link), so a `<Link>`'s own navigation is cancelled. A modified click
     (new tab) never reaches this: the callers let the browser have it. */
  /* "Discard unsaved changes?" — leaving the card and Cancel ask the same. Static `Modal.confirm`
     renders outside the tree, so it does not inherit AppShell's `ConfigProvider locale` — pass both
     button labels or Cancel stays English. */
  const confirmDiscard = onOk =>
    Modal.confirm({
      title: t("edit_discard_confirm"),
      okText: t("edit_discard_ok"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk,
    });
  const leaveTo = (event, to) => {
    event?.preventDefault?.();
    /* The Comments tab's unsent note counts too — it lives here, and leaving drops it. */
    if (!dirty && !commentDraft.trim() && !commentImages.length) return history.push(to);
    confirmDiscard(() => history.push(to));
  };
  const openLinked = (event, otherId) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    leaveTo(event, linkTo(otherId));
  };

  /* The manual note's "Return to automatic mode": one PATCH of the flag alone. The stage stays as it is — the
     next automatic run may change it, which the question says. The card refreshes from the answer
     (the full record), so the overview's note and the head's icon go with it. An API that does not
     know the key yet answers 400: said in a toast, the card untouched. */
  const revertManual = () =>
    Modal.confirm({
      title: t("lc_manual_revert_title"),
      content: t("lc_manual_revert_body").replace("{stage}", t(`lc_${item.lifecycle_status}`)),
      okText: t("lc_manual_revert_ok"),
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () =>
        new Promise(resolve =>
          call({ method: "PATCH", url: `excalibur/counterparties/${id}/`, post: { lifecycle_is_manual: false } }, response => {
            resolve();
            if (isOk(response) && response.id) {
              setItem(response);
              Alert.success(t("lc_manual_reverted_msg"), { position: "top-right" });
              return;
            }
            if (typeof response?.json === "function") Alert.warning(t("lc_manual_refused"), { position: "top-right" });
            else if (response?.detail) Alert.warning(t("edit_error_msg"), { position: "top-right" });
          })
        ),
    });

  /* Save the open field: one request, this field alone. The editor stays open with its draft on
     any failure; a 400 puts the field's one phrase under the control (`lc_manual_refused` for the
     manual flag, `ad_field_invalid` for a typed additional field, `field_error` for a pick), a key
     with nowhere to show falls back to the toast, and a 5xx / network error has already been toasted
     by `action.js` (`failed`). Read from `editorRef`: Enter reaches here from a keydown that may
     have just changed the draft. */
  const save = () => {
    const ed = editorRef.current;
    if (!ed || ed.busy) return;
    const url = `excalibur/counterparties/${id}/`;
    const warn = () => Alert.warning(t("edit_error_msg"), { position: "top-right" });
    /* The response is the full card: it becomes the record; the editor closes. */
    const adopt = response => {
      setItem(response);
      setEditor(prev => {
        dropPreview(prev);
        return null;
      });
      Alert.success(t("edit_saved_msg"), { position: "top-right" });
    };
    const refuse = error => setEditor(prev => (prev ? { ...prev, busy: false, error } : prev));
    const failed = response => {
      if (!response?.failed) warn();
      refuse(null);
    };
    const refusedKeys = (response, onKeys) =>
      response
        .json()
        .catch(() => ({}))
        .then(body => onKeys(body || {}));
    setEditor(prev => (prev ? { ...prev, busy: true, error: null } : prev));

    if (ed.key === "photo") {
      const fd = new FormData();
      fd.append("photo", ed.draft);
      callForm({ method: "PATCH", url, post: fd }, response => {
        /* A 404 hands back a parsed `{detail}`, which `isOk` reads as success — the record is the proof. */
        if (isOk(response) && response.id) return adopt(response);
        if (typeof response?.json !== "function") return failed(response);
        return refusedKeys(response, body => {
          if ("photo" in body) return refuse(t("photo_error"));
          warn();
          return refuse(null);
        });
      });
      return;
    }

    const body = changedFields(withDraft(item, ed), item);
    if (!Object.keys(body).length) return refuse(null);
    call({ method: "PATCH", url, post: body }, response => {
      if (isOk(response) && response.id) return adopt(response);
      if (typeof response?.json !== "function") return failed(response);
      /* A 400, read RAW: `custom_fields` is a list of per-item errors in the order sent — one item
         here — and the other keys are the field's own. */
      return refusedKeys(response, errors => {
        if (ed.key.startsWith("cf:")) {
          const first = Array.isArray(errors.custom_fields) ? errors.custom_fields[0] : null;
          const field = (item.custom_fields || []).find(f => f.id === ed.key.slice(3));
          if (first && Object.keys(first).length) return refuse(t(["choice", "multiselect"].includes(field?.type) ? "field_error" : "ad_field_invalid"));
        } else if (ed.key === "lifecycle") {
          if ("lifecycle_is_manual" in errors) return refuse(t("lc_manual_refused"));
          if ("lifecycle_status" in errors) return refuse(t("field_error"));
        } else if (ed.key in errors) {
          return refuse(t("field_error"));
        }
        warn();
        return refuse(null);
      });
    });
  };

  /* ----------------------------------------------------------------- states */

  if (status === "loading") {
    return (
      <div className="shell-content">
        <Skeleton active title paragraph={{ rows: 2 }} />
        <div className="dp-grid">
          <Skeleton active paragraph={{ rows: 8 }} />
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      </div>
    );
  }

  if (status !== "ready" || !item) {
    const notFound = status === "notfound";
    return (
      <div className="shell-content">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={8} align="center">
              <Typography.Text strong>{notFound ? t("card_not_found_title") : translate(SUPERVISOR_DETAIL, "load_error", lang)}</Typography.Text>
              {notFound ? <Typography.Text type="secondary">{t("card_not_found_desc")}</Typography.Text> : null}
              <Space size={8} wrap>
                {notFound ? null : <Button onClick={() => setReloadKey(k => k + 1)}>{t("retry")}</Button>}
                <Button type="primary" onClick={event => leaveTo(event, backTo)}>
                  {t("card_back")}
                </Button>
              </Space>
            </Flex>
          }
        />
      </div>
    );
  }

  const name = item.name || `#${item.id}`;
  /* The identity strip's meta line — department, supervisor, responsible, external code — each an icon+value
     span rendered ONLY when present (an absent field leaves no gap and no dash placeholder), the
     same icon-led sub-line the visit card (`vd-id__sub`) uses. */

  /* The 90-day order count: a link to the Orders tab (a tab switch, so unsaved edits stay), its
     per-status split in a tooltip on hover AND keyboard focus. */
  const ordersKpi =
    metrics.status === "loading" ? (
      <Skeleton.Input active size="small" className="cp-kpi-skel" />
    ) : metrics.status === "ok" ? (
      <Tooltip
        trigger={["hover", "focus"]}
        title={
          <div className="cp-kpi-tip">
            {ORDER_STATUSES.map(s => (
              <div key={s}>
                {translate(ORDERS_PAGE, `order_status_${s}`, lang)}: <span className="lp-num">{metrics.data.orders_90d_by_status?.[s] ?? 0}</span>
              </div>
            ))}
            <div className="cp-kpi-tip__note">{t("m_orders_no_cancelled")}</div>
          </div>
        }>
        <Button type="link" className="cp-kpi-link" onClick={() => selectTab("orders")}>
          {metrics.data.orders_90d}
        </Button>
      </Tooltip>
    ) : (
      dash
    );
  const supervisorName = fullName(item.assigned_supervisor);
  const hasMeta = Boolean(item.department?.name || supervisorName || item.external_id);

  /* The shared pills (`./status`), bound to this render's language. */
  const statusTag = (tones, key, options) => sharedStatusTag(tones, key, lang, options);
  const lifecycleTags = record => sharedLifecycleTags(record, lang);

  /* The sync line exists only when the record has a sync state at all. A synced record states WHEN
     as one relative phrase (absolute time on hover); a failed one shows the API's `sync_error` as it
     came — it is 1С's own message, cut to one line with the whole text on hover. */
  const syncedAbsolute = item.synced_at ? dayjs(item.synced_at).format("DD.MM.YYYY HH:mm") : undefined;
  const syncedAgo = relativeTime(item.synced_at, lang);
  const syncLine = item.sync_status ? (
    <Flex className="cp-id__sync" align="center" gap="small" wrap>
      {statusTag(SYNC_TONE, item.sync_status, {
        fallback: "off",
        label: item.sync_status === "synced" && syncedAgo ? t("sync_synced_at").replace("{time}", syncedAgo) : undefined,
        title: syncedAbsolute,
      })}
      {item.sync_status === "failed" && item.sync_error ? (
        <span className="cp-id__sync-error" title={item.sync_error}>
          {item.sync_error}
        </span>
      ) : null}
    </Flex>
  ) : null;

  /* Who may change the card — see `CAN_EDIT_ROLES`. */
  const canEdit = CAN_EDIT_ROLES.includes(role);

  /* One writable fact: its name (shown by `facts` above it), its value at rest, and `control` —
     rendered only while THIS fact is open, inside the editor. `InlineField` draws the value button,
     the editor, the buttons, the hint or the error and the keys. */
  const inline = (key, label, value, control, { hint, multiline, clamp } = {}) => (
    <InlineField
      id={fieldId(key)}
      label={label}
      value={value}
      editing={isEditing(key)}
      locked={locked && !isEditing(key)}
      canEdit={canEdit}
      onStart={() => startEdit(key)}
      onCancel={cancelEdit}
      onSave={save}
      busy={isEditing(key) && busy}
      dirty={isEditing(key) && dirty}
      error={isEditing(key) ? editor.error : null}
      hint={hint}
      multiline={multiline}
      clamp={clamp}
      t={t}>
      {isEditing(key) ? control() : null}
    </InlineField>
  );
  /* The control's `status` while its row is refused. */
  const ctlStatus = key => (isEditing(key) && editor.error ? "error" : undefined);
  const describedBy = key => (isEditing(key) && editor.error ? `${fieldId(key)}-error` : undefined);
  /* A dictionary value is a neutral tag, as a picked option in an additional field is. */
  const tagOf = label => (label ? <Tag className="cp-tag">{label}</Tag> : null);
  const dictionaryRow = (key, labelKey) =>
    inline(key, t(labelKey), tagOf(item[key]?.label), () => (
      <EntitySelect
        id={fieldId(key)}
        entity={key}
        extraQuery={DICTIONARY_QUERY[key]}
        value={editor.draft?.id}
        seed={editor.draft}
        placeholder={t(labelKey)}
        status={ctlStatus(key)}
        ariaDescribedBy={describedBy(key)}
        onChange={pickValue}
      />
    ));
  /* A switch with its words beside it — the label (clicking it toggles the switch, as a checkbox's
     does) and a 12px line under it — the lifecycle's manual switch. */
  const setting = (key, checked, onChange, label, hint, { disabled = false } = {}) => (
    <div className={disabled ? "cp-setting cp-setting--off" : "cp-setting"}>
      <Switch id={fieldId(key)} checked={checked} disabled={disabled} onChange={onChange} aria-describedby={hint ? `${fieldId(key)}-hint` : undefined} />
      <div className="cp-setting__text">
        <label className="cp-setting__label" htmlFor={fieldId(key)}>
          {label}
        </label>
        {hint ? (
          <div className="cp-setting__hint" id={`${fieldId(key)}-hint`}>
            {hint}
          </div>
        ) : null}
      </div>
    </div>
  );

  /* Header and Overview split the record between them. The HEAD answers "who is this and where does
     it stand": name, lifecycle (with the manual mark), department, the accountable supervisor, the
     external code, sync, points and balance — a summary, read-only. The OVERVIEW is where the
     card's own facts are changed: how the client is segmented, where it stands and who is
     accountable, the company's additional fields — each row with its pencil — and then how to reach
     and who serves it, which come from the books. Labels sit ABOVE values, so a 30px status pill
     never sits lower than the text beside it. Section heads are the archetype-B `dp-sec-title` /
     `dp-sec-desc` styles. */
  const overviewSection = (key, titleKey, descKey, body) => (
    <section className="cp-ov" key={key} aria-labelledby={`cp-ov-${key}`}>
      <div className="dp-sec-head">
        <h3 className="dp-sec-title" id={`cp-ov-${key}`}>
          {t(titleKey)}
        </h3>
        {descKey ? <p className="dp-sec-desc">{t(descKey)}</p> : null}
      </div>
      {body}
    </section>
  );
  /* A property list — ONE layout for every group (`.cp-props`): two columns of rows, each row the
     name on the left (a fixed share of the row, shown in full, wrapping) and the value on the right,
     a hairline between rows;
     one column below ~1100px of the section's width (a container query). A `wide` row spans both
     columns with the same name width, so every value starts on one vertical. Where the fact is
     writable the value is the button that opens its editor, which floats over the card. */
  const propList = rows => (
    <div className="cp-props">
      {rows.map(([key, label, value, { wide = false } = {}]) => (
        <div className={wide ? "cp-prop cp-prop--wide" : "cp-prop"} key={key}>
          <div className="cp-prop__label">{label}</div>
          <div className="cp-prop__value">{value}</div>
        </div>
      ))}
    </div>
  );
  /* A plain string, or null for "nothing" — `InlineField` draws the empty state. */
  const plain = value => (value === null || value === undefined || value === "" ? null : String(value));
  const people = list =>
    list.length ? (
      <Flex wrap gap="small">
        {list.map(person => (
          <PersonCell key={person.id} user={person} strong={false} />
        ))}
      </Flex>
    ) : null;
  /* "Additional fields": the company's own, set up in the admin (`customFields.js`). No section at
     all when the company has none. Every field, always, in the admin's order — except that text
     fields (multi-line, the wide rows) go last. */
  const customFields = Array.isArray(item.custom_fields) ? item.custom_fields : [];
  const customOrdered = [...customFields.filter(f => f.type !== "text"), ...customFields.filter(f => f.type === "text")];
  const customView = customFields.length
    ? overviewSection(
        "custom",
        "ov_sec_custom",
        null,
        propList(
          customOrdered.map(f => {
            const key = cfKey(f.id);
            const wide = f.type === "text";
            return [
              f.id,
              f.label,
              inline(key, f.label, cfDisplay(f, lang), () => cfEditor({ ...f, value: editor.draft }, setDraft, { lang, status: ctlStatus(key) }), {
                hint: cfHint(f, lang),
                multiline: wide,
                clamp: wide ? CLAMP_LINES : undefined,
              }),
              { wide },
            ];
          })
        )
      )
    : null;
  const overview = (
    <>
      {/* A stage set by hand, said in words with the way back. Calm, not a warning: nothing is
          wrong, it is a mode. */}
      {item.lifecycle_is_manual && item.lifecycle_status ? (
        <div className="cp-manualnote" role="note">
          <UserSwitchOutlined className="cp-manualnote__icon" aria-hidden="true" />
          <div className="cp-manualnote__body">
            <span>{t("lc_manual_banner").replace("{stage}", t(`lc_${item.lifecycle_status}`))}</span>{" "}
            <Button type="link" size="small" className="cp-manualnote__act" onClick={revertManual} disabled={locked}>
              {t("lc_manual_revert")}
            </Button>
          </div>
        </div>
      ) : null}
      {overviewSection(
        "client",
        "ov_sec_client",
        null,
        propList([
          ["client_type", t("k_client_type"), dictionaryRow("client_type", "k_client_type")],
          ["client_category", t("k_client_category"), dictionaryRow("client_category", "k_client_category")],
          ["sales_channel", t("k_sales_channel"), dictionaryRow("sales_channel", "k_sales_channel")],
          [
            "start_cooperation_at",
            t("k_start_cooperation"),
            inline(
              "start_cooperation_at",
              t("k_start_cooperation"),
              item.start_cooperation_at ? dayjs(item.start_cooperation_at).format("DD.MM.YYYY") : null,
              () => (
                /* The API's value is a bare `YYYY-MM-DD` date and clearing it is `null` — never `""`,
                 which is a 400. Shown in the card's own `DD.MM.YYYY`. */
                <DatePicker
                  id={fieldId("start_cooperation_at")}
                  format="DD.MM.YYYY"
                  placeholder={t("k_start_cooperation")}
                  status={ctlStatus("start_cooperation_at")}
                  value={editor.draft ? dayjs(editor.draft) : null}
                  onChange={value => setDraft(value ? value.format("YYYY-MM-DD") : null)}
                />
              )
            ),
          ],
        ])
      )}
      {/* Where the counterparty stands and who is accountable: the lifecycle with its manual /
          automatic mode as ONE editor (the mode has no meaning without a stage), and the supervisor. */}
      {overviewSection(
        "status",
        "ov_sec_status",
        null,
        propList([
          [
            "lifecycle",
            t("k_lifecycle"),
            inline("lifecycle", t("k_lifecycle"), lifecycleTags(item), () => (
              <div className="cp-lcedit">
                <Select
                  id={fieldId("lifecycle")}
                  allowClear
                  status={ctlStatus("lifecycle")}
                  value={editor.draft.lifecycle_status ?? undefined}
                  placeholder={t("k_lifecycle")}
                  options={LIFECYCLE_STATUSES.map(value => ({ value, label: t(`lc_${value}`) }))}
                  onChange={value => setStage(value ?? null)}
                />
                {/* Manual mode needs a lifecycle value: without one the switch is off, locked, and
                      says so. */}
                {setting(
                  "lifecycle_is_manual",
                  Boolean(editor.draft.lifecycle_status) && Boolean(editor.draft.lifecycle_is_manual),
                  checked => setLifecycle({ lifecycle_is_manual: checked }),
                  t("lc_manual_switch"),
                  t(editor.draft.lifecycle_status ? "lc_manual_switch_hint" : "lc_manual_needs_stage"),
                  { disabled: !editor.draft.lifecycle_status }
                )}
              </div>
            )),
          ],
          [
            "assigned_supervisor",
            t("k_supervisor"),
            inline(
              "assigned_supervisor",
              t("k_supervisor"),
              item.assigned_supervisor ? <PersonCell user={item.assigned_supervisor} strong={false} /> : null,
              () => (
                <EntitySelect
                  id={fieldId("assigned_supervisor")}
                  entity="supervisor"
                  extraQuery={SUPERVISOR_QUERY}
                  avatar
                  value={editor.draft?.id}
                  seed={editor.draft}
                  placeholder={t("k_supervisor")}
                  status={ctlStatus("assigned_supervisor")}
                  ariaDescribedBy={describedBy("assigned_supervisor") || `${fieldId("assigned_supervisor")}-hint`}
                  onChange={pickSupervisor}
                />
              ),
              { hint: t("k_supervisor_hint") }
            ),
          ],
        ])
      )}
      {customView}
      {/* From the books, so read-only here — the API refuses a write to any of it. The note names
          no system on purpose (which books feed this deployment is the deployment's business). */}
      {overviewSection(
        "contacts",
        "ov_sec_contacts",
        "ov_sec_account_desc",
        propList([
          ["phone_number", t("k_phone"), <InlineField key="phone_number" canEdit={false} value={plain(item.phone_number)} t={t} />],
          [
            "merchandisers",
            t("k_responsible"),
            <InlineField
              key="merchandisers"
              canEdit={false}
              value={people(Array.isArray(item.merchandisers) ? item.merchandisers.filter(fullName) : [])}
              t={t}
            />,
          ],
          /* The address is the long one: a wide row, last, cut to a few lines. */
          ["address", t("k_address"), <InlineField key="address" canEdit={false} value={plain(item.address)} clamp={CLAMP_LINES} t={t} />, { wide: true }],
        ])
      )}
    </>
  );

  const activeSection = SECTIONS.find(s => s.key === tab);
  /* The document tabs' window comes from `metrics/` (the same date their counters are counted from):
     until it has arrived they wait; if it failed, their Retry re-asks for it. */
  const windowProps = {
    windowFrom: metrics.status === "ok" ? metrics.data.window_from : null,
    windowError: metrics.status === "error",
    onRetryWindow: loadMetrics,
  };
  /* Per-tab counts on the rail — none until `metrics/` has answered (no "0", no spinner). */
  const counts = metrics.status === "ok" ? metrics.data.counts || null : null;
  const sectionBody =
    activeSection.key === "contacts" ? (
      <ContactsTab counterpartyId={item.id} lang={lang} onCountsChange={loadMetrics} />
    ) : activeSection.key === "addresses" ? (
      <AddressesTab
        item={item}
        lang={lang}
        leaveTo={leaveTo}
        onPoints={mergePoints}
        onPointAdded={mergePoint}
        onCreatePoint={openCreatePoint}
        createIntent={createIntent}
        onIntentDone={createIntentDone}
        onCountsChange={loadMetrics}
      />
    ) : activeSection.key === "orders" ? (
      <OrdersTab
        counterpartyId={item.id}
        lang={lang}
        cardLocation={{ pathname: location.pathname, search: "?tab=orders", state: location.state }}
        leaveTo={leaveTo}
        {...windowProps}
      />
    ) : activeSection.key === "returns" ? (
      <ReturnsTab
        counterpartyId={item.id}
        lang={lang}
        cardLocation={{ pathname: location.pathname, search: "?tab=returns", state: location.state }}
        leaveTo={leaveTo}
        {...windowProps}
      />
    ) : activeSection.key === "payments" ? (
      <PaymentsTab counterpartyId={item.id} lang={lang} leaveTo={leaveTo} debtTo={debtTo} {...windowProps} />
    ) : activeSection.key === "comments" ? (
      <CommentsTab
        counterpartyId={item.id}
        lang={lang}
        draft={commentDraft}
        setDraft={setCommentDraft}
        images={commentImages}
        setImages={setCommentImages}
        onCountsChange={loadMetrics}
      />
    ) : activeSection.key === "debts" ? (
      <DebtsTab counterpartyId={item.id} lang={lang} highlightId={debtParam} />
    ) : (
      <>
        {overview}
        <LinksSection
          counterpartyId={item.id}
          links={Array.isArray(item.linked_counterparties) ? item.linked_counterparties : []}
          lang={lang}
          onChange={mergeLinks}
          linkTo={linkTo}
          onOpen={openLinked}
        />
      </>
    );

  return (
    <div className="shell-content cp-detail">
      {/* Command bar — breadcrumb + actions. Non-sticky on purpose: the card is short and has a
          couple of actions, so it skips the visit page's measured sticky-offset machinery (the same
          call `/supervisors/:id` and `/answers` made). */}
      <Flex className="cp-bar" align="center" gap="middle" wrap>
        <Flex className="cp-bar__crumb" align="center" gap={6}>
          <Typography.Link href={history.createHref({ pathname: "/sales/counterparties", search: backSearch })} onClick={linkClick(leaveTo, backTo)}>
            {translate(SIDE_NAV, "counterparties", lang)}
          </Typography.Link>
          <RightOutlined className="cp-crumb__sep" />
          <Typography.Text strong ellipsis>
            {name}
          </Typography.Text>
        </Flex>
        <div className="cp-spacer" />
        <Space size={8} wrap>
          <Button icon={<LinkOutlined />} onClick={copyLink}>
            {t("card_copy_link")}
          </Button>
        </Space>
      </Flex>

      {/* Identity card — the mockup's header shape: an info row (square shot + icon-led title/meta)
          over a divided metrics strip. The same building blocks as the visit card (`vd-id`), only
          the metrics move under a rule instead of sitting in a right-hand block. */}
      <Card className="cp-id" variant="outlined">
        <div className={isEditing("photo") ? "cp-id__top cp-id__top--edit" : "cp-id__top"}>
          {/* The photo's pencil sits under the shot and opens the file dialog (`Upload` fires it from
              its child's click); a chosen file replaces the shot with its preview and puts Save /
              Cancel under it — the same editor shape as a row, minus a control. Esc cancels here too. */}
          <Flex
            vertical
            align="center"
            gap={8}
            className="cp-id__shot"
            onKeyDown={event => {
              if (isEditing("photo") && event.key === "Escape") {
                event.preventDefault();
                cancelEdit();
              }
            }}>
            <Avatar
              shape="square"
              size={52}
              className="cp-logo"
              style={{ fontSize: 20 }}
              src={(isEditing("photo") && editor.preview) || item.photo_url || null}
              onError={() => true}>
              {initialsOf(name)}
            </Avatar>
            {canEdit && !isEditing("photo") ? (
              <Upload
                beforeUpload={file => {
                  if (!locked) stagePhoto(file);
                  return false;
                }}
                maxCount={1}
                showUploadList={false}
                accept="image/*"
                disabled={locked}>
                <Tooltip title={locked ? t("inline_locked_tip") : undefined}>
                  <button
                    type="button"
                    className={locked ? "cp-photo-btn ifv ifv__btn ifv--locked" : "cp-photo-btn ifv ifv__btn"}
                    aria-label={t("inline_edit_aria").replace("{field}", t("k_photo"))}
                    aria-disabled={locked || undefined}>
                    <EditOutlined className="ifv__pen" aria-hidden="true" />
                    <span>{t("k_photo")}</span>
                  </button>
                </Tooltip>
              </Upload>
            ) : null}
            {isEditing("photo") ? (
              <>
                {editor.error ? (
                  <div className="ifv-ed__error cp-photo-error" role="alert">
                    {editor.error}
                  </div>
                ) : null}
                <Flex gap={8} wrap justify="center">
                  <Button type="primary" size="small" loading={busy} disabled={!dirty} onClick={save} autoFocus>
                    {t("edit_save")}
                  </Button>
                  <Button size="small" disabled={busy} onClick={cancelEdit}>
                    {t("edit_cancel")}
                  </Button>
                </Flex>
              </>
            ) : null}
          </Flex>

          <div className="cp-id__main">
            <Flex className="cp-id__head" align="center" gap={12} wrap>
              <Typography.Text strong className="cp-id__t">
                {name}
              </Typography.Text>
              {lifecycleTags(item)}
            </Flex>
            {hasMeta ? (
              <Flex className="cp-id__sub" align="center" gap="small" wrap>
                {item.department?.name ? (
                  <span>
                    <ApartmentOutlined /> {item.department.name}
                  </span>
                ) : null}
                {/* The ACCOUNTABLE person only. The merchandisers who serve the client are listed,
                    with faces, in the Overview — once, not here as well. `TeamOutlined` is the
                    supervisors glyph in spec/02's icon map; the span names the relation on hover. */}
                {supervisorName ? (
                  <span title={t("k_supervisor")}>
                    <TeamOutlined /> {supervisorName}
                  </span>
                ) : null}
                {item.external_id ? (
                  <span>
                    <NumberOutlined /> {item.external_id}
                  </span>
                ) : null}
              </Flex>
            ) : null}
            {syncLine}
          </div>
        </div>

        {/* Metrics — the SHARED `KpiStrip` the order / return detail headers use (`.dp-kpi*`): equal
            centred tiles, a divider between them and its own top rule; 2 × 2 on a phone
            (`counterparties.scss`), the shared stack left to the order / return pages. */}
        <KpiStrip
          items={[
            { key: "orders", label: t("m_orders_90d"), value: ordersKpi },
            { key: "remaining", label: t("m_remaining"), value: money(item.remaining_funds) },
            /* `points_count`: every point not deleted, inactive ones included — not the length of
               `points`, which is the same set today but is the block's list, not the count. */
            { key: "points", label: t("m_points"), value: item.points_count ?? 0 },
            /* `item` is the saved record — a draft in an open editor is not yet the counterparty's. */
            {
              key: "since",
              label: t("m_cooperation_since"),
              value: item.start_cooperation_at ? <span className="lp-num">{dayjs(item.start_cooperation_at).format("DD.MM.YYYY")}</span> : dash,
            },
          ]}
        />
        {/* AD-4, on every tab: without a point the counterparty cannot be worked in the field. */}
        {item.points_count === 0 ? <NoPointsNotice lang={lang} className="cp-nopoints" onGo={openCreatePoint} /> : null}
      </Card>

      {/* Section rail + section card — the visit page's `vd-rail` / `vd-sec` shape (its own `cp-*`
          classes, since `visit-detail.scss` is not globally imported). One section mounted at a
          time; the rail is non-sticky here (short page, no measured-offset machinery). */}
      <div className="cp-rail" role="tablist" aria-label={translate(VISIT_PAGE, "sections", lang)}>
        {SECTIONS.map(section => (
          <button key={section.key} type="button" role="tab" aria-selected={section.key === tab} onClick={() => selectTab(section.key)}>
            {section.icon}
            {t(section.labelKey)}
            {counts && Number.isFinite(counts[section.key]) ? <span className="cp-rail__n">{counts[section.key]}</span> : null}
          </button>
        ))}
      </div>

      <Card className="cp-sec" variant="outlined" role="tabpanel">
        {/* One heading per section; the Orders and Returns tabs hang "Read-only", their window and
            the way to the full list on it rather than repeating their own title above the table. */}
        <Flex className="cp-sec__h" align="center" justify="space-between" gap="small" wrap>
          <Flex align="center" gap="small">
            {activeSection.icon}
            {t(activeSection.labelKey)}
            {DOC_TABS[activeSection.key] ? <ReadOnlyTag lang={lang} /> : null}
          </Flex>
          {DOC_TABS[activeSection.key] ? (
            <TabHead counterpartyId={item.id} counterpartyName={name} lang={lang} leaveTo={leaveTo} {...DOC_TABS[activeSection.key]} />
          ) : null}
        </Flex>
        {sectionBody}
      </Card>
    </div>
  );
};

const Page = () => {
  const { id } = useParams();
  return <CardPage key={id} id={id} />;
};

export default Page;
